import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone } from 'lucide-react';

import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useSession } from '@/hooks/useSession';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/cn';
import {
  getMaxBridge,
  RequestContactUnavailableError,
} from '@/lib/max-bridge';
import { extractDigits, formatPhone, isValidPhone, normalizePhone } from '@/lib/phone-mask';
import { ApiClientError, createLead } from '@/lib/api-client';
import type { CreateLeadRequest } from '@/lib/api-client';
import {
  PD_CONSENT_VERSION,
  PRIVACY_POLICY_VERSION,
  MARKETING_CONSENT_VERSION,
} from '@/lib/legal-versions';

/**
 * Экран 7 — Сбор контактов и согласий.
 *
 * Дизайн phase-2.5 (UA 30.04.2026):
 *  - Зелёный pill «✓ Готов» сверху.
 *  - Заголовок «Цифровой профиль готов» + подзаголовок «Оставьте номер —
 *    покажем результат и продублируем в сообщении, чтобы не потерялся.»
 *  - Светло-серый input «Имя» (placeholder).
 *  - Тёмно-синяя primary с 📞 «Поделиться номером из MAX».
 *  - Сразу видно «или ввести вручную» divider + поле телефона с маской.
 *  - Маркетинговый чекбокс остаётся (опциональный, по discrepancies №20
 *    обязательный чекбокс ПДн зафиксирован на Welcome через implied consent).
 *  - CTA «Получить результат» серый/оранжевый по validation.
 */

type PhoneStrategy =
  | 'idle' // ничего не нажимали
  | 'requesting' // нативный диалог открыт
  | 'native-success' // номер получен через MAX
  | 'manual-after-cancel' // ручной ввод после cancel в нативном
  | 'manual-fallback'; // ручной ввод после technical unavailable

/** Тип inline-ошибки submit (показ под кнопкой). */
type SubmitErrorKind = 'validation' | 'auth' | 'rate_limit' | null;

const SUBMIT_ERROR_TEXT: Record<Exclude<SubmitErrorKind, null>, string> = {
  validation: 'Проверьте, что имя и телефон введены корректно.',
  auth: 'Не удалось подтвердить сессию MAX. Откройте Mini App заново.',
  rate_limit: 'Слишком много попыток. Подождите минуту и попробуйте снова.',
};

export default function ContactsForm() {
  const navigate = useNavigate();
  const {
    state,
    setContacts,
    setLeadId,
    setOutbox,
  } = useSession();
  const { track } = useAnalytics();

  const [name, setName] = useState(state.contacts?.name ?? '');
  const [phoneStrategy, setPhoneStrategy] = useState<PhoneStrategy>('idle');
  const [nativePhone, setNativePhone] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitErrorKind>(null);

  // Phase A P0 PR B: pre-compute result_summary в фоне (lazy import,
  // не блокирует submit — родитель может кликнуть до того как compute
  // завершится; в этом случае payload идёт без result_summary,
  // backend fallback на минимальный note).
  //
  // Lazy imports — `buildResult` (~300 строк) + `data/{age-group}/*` —
  // нужны для composer'а. Phase 3 stage 1 bundle hygiene: эти модули
  // должны быть в lazy chunk, не в main. На ContactsForm родитель
  // тратит 30-90 сек (имя + телефон + чекбоксы) → chunk успеет
  // подгрузиться.
  const [precomputedSummary, setPrecomputedSummary] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!state.child || !state.ageGroup) return;
    let cancelled = false;
    (async () => {
      try {
        const [
          { buildResult, buildResultSummary },
          { calculateStage1, calculateStage2 },
          { getTestData },
        ] = await Promise.all([
          import('@/lib/result/build-result'),
          import('@/lib/scoring'),
          import('@/data'),
        ]);
        if (cancelled || !state.child || !state.ageGroup) return;
        const testData = getTestData(state.ageGroup);
        if (!testData) return;
        const s1 = calculateStage1({ answers: state.stage1Answers, testData });
        const s2 = calculateStage2({ answers: state.stage2Answers, testData });
        const result = buildResult({
          child: state.child,
          stage1Result: s1,
          stage2Result: s2,
          ageGroup: state.ageGroup,
        });
        const summary = buildResultSummary({
          result,
          stage2Result: s2,
          childName: state.child.name,
          childAge: state.child.age,
          ageGroup: state.ageGroup,
          sessionId: state.sessionId,
        });
        if (!cancelled) setPrecomputedSummary(summary);
      } catch {
        // ignore — submit пройдёт без result_summary, backend fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    track('contact_screen_view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRequestNative() {
    setPhoneStrategy('requesting');
    try {
      const result = await getMaxBridge().requestContact();
      const normalized = normalizePhone(result.phone) ?? result.phone;
      setNativePhone(normalized);
      setPhoneStrategy('native-success');
    } catch (err) {
      if (err instanceof RequestContactUnavailableError) {
        setPhoneStrategy('manual-fallback');
      } else {
        track('contact_native_declined');
        setPhoneStrategy('manual-after-cancel');
      }
    }
  }

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length >= 2;
  const phoneE164 =
    phoneStrategy === 'native-success' ? nativePhone : normalizePhone(manualInput);
  const isPhoneValid =
    phoneStrategy === 'native-success' ? Boolean(nativePhone) : isValidPhone(manualInput);
  const canSubmit = isNameValid && isPhoneValid;

  async function buildLeadPayload(parentName: string, parentPhone: string): Promise<CreateLeadRequest | null> {
    if (!state.child || !state.ageGroup) return null;

    // Phase 4 day 2: derived поля компонент читает из state — Stage2Question
    // финализирует их ПЕРЕД navigate('/contacts'). Если derivedResult по
    // какой-то причине null (legacy persisted state без поля; race; M5
    // resetAnswers с переходом на /contacts), используем lazy import
    // computeDerivedResult — расчёт остаётся в lazy chunk, в main не попадает.
    let topDirections: string[] = state.derivedResult?.topDirections ?? [];
    let skillLevel: string = state.derivedResult?.skillLevel ?? '';
    if (state.derivedResult === null) {
      const { computeDerivedResult } = await import('@/lib/derived-result');
      const computed = computeDerivedResult(state);
      if (computed) {
        topDirections = computed.topDirections;
        skillLevel = computed.skillLevel;
      }
    }

    // Оба consent timestamps выставлены на Welcome screen (Commit 1
    // PR Consents). Если по какой-то причине null — recovery guard
    // в SessionContext (Commit 3) уже редиректнул на /. Здесь
    // defensive fallback на now() — backend Zod refine откажет если
    // что-то рассинхрилось (Commit 4 → 400 VALIDATION).
    const nowIso = new Date().toISOString();
    const pdAcceptedAt = state.consents.dataConsentAt
      ? new Date(state.consents.dataConsentAt).toISOString()
      : nowIso;
    const marketingAcceptedAt = state.consents.marketingConsentAt
      ? new Date(state.consents.marketingConsentAt).toISOString()
      : nowIso;

    // result_summary берётся из useEffect pre-compute выше. Если не успел
    // resolve до клика «Получить результат» — undefined → payload без поля
    // → backend INSERT NULL → worker.ts fallback на минимальный note.
    return {
      child_name: state.child.name,
      child_age: state.child.age,
      parent_name: parentName,
      parent_phone: parentPhone,
      consents: {
        pd_consent: true,
        pd_consent_version: PD_CONSENT_VERSION,
        pd_consent_accepted_at: pdAcceptedAt,
        privacy_policy_version: PRIVACY_POLICY_VERSION,
        marketing_consent: true,
        marketing_consent_version: MARKETING_CONSENT_VERSION,
        marketing_consent_at: marketingAcceptedAt,
      },
      session_id: state.sessionId,
      top_directions: topDirections,
      skill_level: skillLevel,
      raw_answers: {
        stage1: state.stage1Answers,
        stage2: state.stage2Answers,
      },
      ...(precomputedSummary !== undefined ? { result_summary: precomputedSummary } : {}),
    };
  }

  async function handleSubmit() {
    if (submitting) return;
    if (!canSubmit || !phoneE164) {
      track('contact_submit_error', {
        error_type: !isNameValid ? 'name_invalid' : 'phone_invalid',
      });
      return;
    }

    setSubmitError(null);
    setContacts({
      name: trimmedName,
      phone: phoneE164,
      submittedAt: Date.now(),
    });
    track('contact_submit_success', {
      used_native_phone: phoneStrategy === 'native-success',
    });

    const payload = await buildLeadPayload(trimmedName, phoneE164);
    if (!payload) {
      // Защитный путь: state без child/ageGroup сюда дойти не должен (проход
      // через ProtectedRoute блокирует), но если всё-таки — сразу /loading,
      // ведь клиент-сайд уже считает контакты submitted.
      navigate('/loading');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createLead(payload);
      setLeadId(response.lead_id);
      navigate('/loading');
    } catch (err) {
      if (err instanceof ApiClientError) {
        const kind = err.payload.kind;
        if (kind === 'validation' || kind === 'auth' || kind === 'rate_limit') {
          // Inline error path: не сохраняем в outbox (не имеет смысла
          // ретраить плохой запрос / просроченный initData / пользовательский
          // лимит). Пользователь правит и пробует снова.
          track('contact_submit_error', { error_type: kind });
          setSubmitError(kind);
          setSubmitting(false);
          return;
        }
        if (kind === 'temporarily_unavailable') {
          // Kill switch / idempotency lookup fail. Outbox retry бесполезен —
          // backend сам отказывается. Фуллскрин Error с кнопкой "Закрыть"
          // (без retry — пока flag активен, retry не помогает).
          track('contact_submit_error', { error_type: kind });
          navigate('/error', {
            state: { errorType: 'temporarily_unavailable', screenId: 'contacts' },
          });
          return;
        }
        // network / server → outbox path. Идём на /loading, который через
        // ~2с уведёт на /result. На /result mount запустится retry useEffect.
        track('contact_submit_error', { error_type: kind });
        setOutbox(payload);
        navigate('/loading');
        return;
      }
      // Неизвестная ошибка — на всякий случай в outbox (network-like).
      track('contact_submit_error', { error_type: 'unknown' });
      setOutbox(payload);
      navigate('/loading');
    }
  }

  const showManualInput =
    phoneStrategy === 'manual-after-cancel' ||
    phoneStrategy === 'manual-fallback' ||
    phoneStrategy === 'idle' ||
    phoneStrategy === 'requesting';

  return (
    <ScreenLayout
      cta={
        <div className="flex flex-col gap-2">
          {submitError && (
            <p
              role="alert"
              className="text-sm leading-snug text-error"
            >
              {SUBMIT_ERROR_TEXT[submitError]}
            </p>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            fullWidth
          >
            {submitting ? 'Отправляем…' : 'Получить результат'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Зелёный success-pill «Готов» — насыщенный фон */}
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-success/20 px-3 py-1.5 text-xs font-semibold text-success">
          <CheckIcon />
          Готов
        </span>

        <header className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold leading-tight text-text-primary">
            Цифровой профиль готов
          </h2>
          <p className="text-base leading-relaxed text-text-secondary">
            Оставьте номер — покажем результат и продублируем в сообщении, чтобы не потерялся.
          </p>
        </header>

        <Input
          label="Имя родителя"
          placeholder="Имя"
          value={name}
          onChange={setName}
          autoComplete="given-name"
          inputMode="text"
        />

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text-primary">Телефон</span>

          {/* Native-success состояние — badge с номером, ничего больше */}
          {phoneStrategy === 'native-success' && (
            <div
              className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-base text-text-primary"
              aria-live="polite"
            >
              ✓ {formatPhone(nativePhone ?? '')} получен
            </div>
          )}

          {/* Тёмно-синяя primary-кнопка «Поделиться номером из MAX».
              Показывается только в idle/requesting. Для native-success виден badge
              с номером, для manual-after-cancel и manual-fallback — inline-нотис
              + поле ручного ввода (кнопка скрыта, чтобы не создавать ложное
              ожидание восстановления MAX-канала). */}
          {(phoneStrategy === 'idle' || phoneStrategy === 'requesting') && (
            <button
              type="button"
              onClick={handleRequestNative}
              disabled={phoneStrategy === 'requesting'}
              className={cn(
                'flex h-12 w-full items-center justify-center gap-2 rounded-2xl px-5 text-base font-semibold no-tap-highlight',
                'bg-primary text-white active:opacity-90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-opacity duration-150',
              )}
            >
              <Phone size={20} strokeWidth={2} />
              <span>
                {phoneStrategy === 'requesting' ? 'Запрашиваем…' : 'Поделиться номером из MAX'}
              </span>
            </button>
          )}

          {/* Inline-сообщение для cancel */}
          {phoneStrategy === 'manual-after-cancel' && (
            <p className="text-xs leading-relaxed text-text-secondary">
              Не удалось получить номер из MAX. Введите вручную ниже.
            </p>
          )}
          {phoneStrategy === 'manual-fallback' && (
            <p className="text-xs leading-relaxed text-text-secondary">
              MAX не может поделиться номером. Введите его вручную.
            </p>
          )}

          {/* «или ввести вручную» divider + поле — видно сразу */}
          {showManualInput && (
            <>
              {(phoneStrategy === 'idle' || phoneStrategy === 'requesting') && (
                <div className="flex items-center gap-3 my-1" aria-hidden="true">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-text-secondary">или ввести вручную</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              <Input
                value={formatPhone(manualInput)}
                onChange={(v) => setManualInput(extractDigits(v))}
                placeholder="+7 (___) ___-__-__"
                inputMode="tel"
                autoComplete="tel"
                type="tel"
                aria-label="Телефон"
              />
            </>
          )}
        </div>
      </div>
    </ScreenLayout>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 6.5l2.5 2.5L10 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
